import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Upload, Image as ImageIcon, X, Check, Loader2, Camera } from 'lucide-react';

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Piccolo picker inline per associare un'immagine a un valore di variante.
 *
 * Props:
 *  - value: URL immagine attualmente associata (o null)
 *  - onChange(newUrl | null): callback quando l'associazione cambia
 *  - baseImages: array URL immagini prodotto già caricate (carosello principale)
 *  - onBaseImagesChange(newBaseImages): callback quando aggiungiamo una nuova
 *      immagine al carosello (upload da qui)
 *  - bucketName: bucket Supabase per upload
 *  - variantLabel: etichetta variante (es. "Rosso") mostrata in UI
 */
export const VariantImagePicker = ({
  value,
  onChange,
  baseImages = [],
  onBaseImagesChange,
  bucketName = 'products',
  variantLabel = '',
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      toast.error('Formato file non valido. Usa JPG, PNG o WebP');
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File troppo grande. Massimo 5MB');
      return false;
    }
    return true;
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!validateFile(file)) {
      event.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `${bucketName}/${fileName}`;

      const { error } = await supabase.storage
        .from('rarifex-uploads')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('rarifex-uploads')
        .getPublicUrl(filePath);

      // Aggiungi al carosello principale (evita duplicati)
      if (!baseImages.includes(publicUrl) && typeof onBaseImagesChange === 'function') {
        onBaseImagesChange([...baseImages, publicUrl]);
      }
      onChange(publicUrl);
      setPickerOpen(false);
      toast.success('Immagine associata alla variante');
    } catch (err) {
      console.error('Variant image upload error:', err);
      toast.error('Errore nel caricamento immagine');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleSelectExisting = (url) => {
    onChange(url);
    setPickerOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        className="relative group w-14 h-14 rounded-lg border-2 border-dashed border-slate-300 hover:border-[#008080] hover:bg-teal-50/30 transition-all flex items-center justify-center overflow-hidden bg-white flex-shrink-0"
        data-testid={`variant-image-picker-${variantLabel}`}
        title={value ? 'Cambia immagine variante' : 'Associa immagine alla variante'}
      >
        {value ? (
          <>
            <img
              src={value}
              alt={variantLabel}
              className="w-full h-full object-cover"
            />
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => e.key === 'Enter' && handleClear(e)}
              className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              aria-label="Rimuovi immagine"
            >
              <X className="w-2.5 h-2.5" />
            </span>
          </>
        ) : (
          <Camera className="w-5 h-5 text-slate-400 group-hover:text-[#008080]" />
        )}
      </button>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl" data-testid="variant-image-picker-dialog">
          <DialogHeader>
            <DialogTitle>
              Associa immagine a: <span className="text-[#008080]">{variantLabel}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Galleria immagini già caricate */}
            {baseImages.length > 0 && (
              <div>
                <p className="text-sm text-slate-600 mb-2">
                  Scegli tra le immagini già caricate nel carosello prodotto:
                </p>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {baseImages.map((img, idx) => {
                    const isSelected = value === img;
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSelectExisting(img)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          isSelected
                            ? 'border-[#008080] ring-2 ring-[#008080]/30'
                            : 'border-slate-200 hover:border-slate-400'
                        }`}
                        data-testid={`variant-image-choice-${idx}`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        {isSelected && (
                          <div className="absolute inset-0 bg-[#008080]/20 flex items-center justify-center">
                            <div className="w-6 h-6 rounded-full bg-[#008080] text-white flex items-center justify-center">
                              <Check className="w-4 h-4" />
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Separatore */}
            {baseImages.length > 0 && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-2 text-slate-500 uppercase tracking-wider">
                    oppure
                  </span>
                </div>
              </div>
            )}

            {/* Upload nuova immagine */}
            <div>
              <p className="text-sm text-slate-600 mb-2">
                Carica una nuova immagine (verrà aggiunta anche al carosello prodotto):
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleUpload}
                className="hidden"
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                variant="outline"
                className="w-full"
                data-testid="variant-image-upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Caricamento...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Carica nuova immagine
                  </>
                )}
              </Button>
            </div>

            {/* Empty state se non ci sono immagini */}
            {baseImages.length === 0 && !uploading && (
              <div className="text-center py-4 text-slate-500 text-sm border-2 border-dashed border-slate-200 rounded-lg">
                <ImageIcon className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                Nessuna immagine prodotto ancora caricata.<br />
                Carica una nuova immagine qui sopra.
              </div>
            )}

            {value && (
              <div className="pt-3 border-t">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { onChange(null); setPickerOpen(false); }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="variant-image-remove-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Rimuovi associazione
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VariantImagePicker;
